import { useRef, useState } from 'react';
import { cloneDeep } from 'lodash';
import { useFetchPartners } from '../hooks/useFetchPartners';
import { sortPartnersByLetter } from '../utils/partners';
import { PartnerDetail, YSH_GTM_EVENTS } from '../types/types';
import { useFetchRecommendedPartners } from '../hooks/useFetchRecommendedPartners.ts';
import { IoChevronDown } from 'react-icons/io5';
import { pushToAnalytics } from '../utils/analytics.ts';
import PartnerItem from './PartnerItem.tsx';
import PartnerLetter from './PartnerLetter.tsx';
import PartnerSearchInput from './PartnerSearchInput.tsx';
import Button from './Button.tsx';
import '../scss/filter-list-checkbox.scss';
import '../scss/filter-list-container.scss';
import '../scss/filter-partner-with-favorites.scss';

interface Props {
  userSelectedPartners: PartnerDetail[];
  setUserSelectedPartners: (partners: PartnerDetail[]) => void;
  handleSaveFilteredListOfFavouriteBrands: () => void;
  placement: string;
}

const FilterPartnerWithFavorites: React.FC<Props> = ({
  userSelectedPartners,
  setUserSelectedPartners,
  handleSaveFilteredListOfFavouriteBrands,
  placement
}) => {
  const [showMoreRecommended, setShowMoreRecommended] = useState(false);
  const [expandAllPartnersList, setExpandAllPartnersList] = useState(false);
  const [filterAllPartnersByValue, setFilterAllPartnersByValue] = useState('');

  const { data: partners } = useFetchPartners();
  const { data: recommendedPartners } = useFetchRecommendedPartners();

  const dropdownRef = useRef<HTMLDivElement>(null);

  const sortedPartners = sortPartnersByLetter(partners || []);

  const handlePartnerClick = (partner: PartnerDetail) => {
    if (userSelectedPartners.some(selectedPartner => selectedPartner.id === partner.id)) {
      setUserSelectedPartners(userSelectedPartners.filter(p => p.id !== partner.id));
      return;
    }

    const newlySelectedPartners = cloneDeep(userSelectedPartners);
    newlySelectedPartners.push(partner);

    setUserSelectedPartners(newlySelectedPartners);
  };

  const handleExpandAllPartnersList = () => {
    if (!expandAllPartnersList) {
      setFilterAllPartnersByValue('');

      pushToAnalytics({
        eventName: YSH_GTM_EVENTS.SHOW_ALL_BRANDS,
        placement,
        data: {}
      });
    }

    setExpandAllPartnersList(value => !value);
  };

  return (
    <section className="filter-partner-with-favorites">
      <div className="filter-partner-with-favorites__content pt-2 font-yettel">
        <section className="filter-partner-with-favorites__recommended">
          <p
            className="font-yettel-bold mb-2 full-width d-flex justify-content-between mt-2"
            onClick={() => handleExpandAllPartnersList()}
          >
            Preporučeni brendovi
            <span className="d-inline-block">
              <Chevron active={!expandAllPartnersList} />
            </span>
          </p>

          {!expandAllPartnersList && (
            <>
              <div className="filter-partner-with-favorites__recommended__content pt-2 font-yettel">
                {recommendedPartners?.map((recommendedPartner: PartnerDetail, index: number) => {
                  if (showMoreRecommended || (!showMoreRecommended && index < 10)) {
                    return (
                      <PartnerItem
                        key={recommendedPartner.id}
                        partner={recommendedPartner}
                        isPartnerSelected={userSelectedPartners.some(
                          selectedPartner => selectedPartner.id === recommendedPartner.id
                        )}
                        handlePartnerClick={handlePartnerClick}
                        index={index}
                      />
                    );
                  }
                })}
              </div>
              <p
                onClick={() => setShowMoreRecommended(value => !value)}
                className="text-decoration-underline cursor-pointer font-yettel-bold fz-14 mt-2"
              >
                {showMoreRecommended ? 'Prikaži manje' : 'Prikaži više'}
              </p>
            </>
          )}
        </section>

        <section className="filter-partner-with-favorites__all mt-3" ref={dropdownRef}>
          <div className="filter-partner-with-favorites__all__heading">
            <hr />
            <p
              className="font-yettel-bold mb-2 full-width d-flex justify-content-between mt-2"
              onClick={() => handleExpandAllPartnersList()}
            >
              Svi brendovi
              <span className="d-inline-block">
                <Chevron active={expandAllPartnersList} />
              </span>
            </p>

            {expandAllPartnersList && <PartnerSearchInput setFilterValue={setFilterAllPartnersByValue} />}
          </div>
          <section
            className="filter-partner-with-favorites__all__content"
            style={expandAllPartnersList ? { display: 'flex' } : { display: 'none' }}
          >
            {partners &&
              sortedPartners.letters.map(letter => (
                <>
                  <PartnerLetter key={letter} letter={letter} filteredView={Boolean(filterAllPartnersByValue.length)}>
                    {sortedPartners.items[letter]?.map(partner => {
                      if (
                        filterAllPartnersByValue.length === 0 ||
                        (filterAllPartnersByValue.length &&
                          partner.name.toLowerCase().startsWith(filterAllPartnersByValue.toLowerCase()))
                      ) {
                        return (
                          <PartnerItem
                            key={partner.id}
                            partner={partner}
                            isPartnerSelected={userSelectedPartners.some(
                              selectedPartner => selectedPartner.id === partner.id
                            )}
                            handlePartnerClick={handlePartnerClick}
                            index={partner.index as number}
                          />
                        );
                      }
                    })}
                  </PartnerLetter>
                  {!filterAllPartnersByValue && <hr />}
                </>
              ))}
          </section>
        </section>

        <section className="filter-partner-with-favorites__button pt-4 pb-5">
          <Button onClick={() => handleSaveFilteredListOfFavouriteBrands()} className="text-center">
            Dodaj ({userSelectedPartners.length})
          </Button>
        </section>
      </div>
    </section>
  );
};

interface ChevronProps {
  active: boolean;
}

const Chevron: React.FC<ChevronProps> = ({ active }) => {
  return (
    <IoChevronDown
      size={18}
      style={
        active ? { transform: 'rotate(180deg)', transition: '0.3s' } : { transform: 'rotate(0deg)', transition: '0.3s' }
      }
    />
  );
};

export default FilterPartnerWithFavorites;
